import { AuthPanel, getAuthenticatedUser } from '@/components/auth';
import { useCollection } from '@/features/events/contexts/CollectionContext/useCollection';
import type { LaunchOption, LaunchParams } from '@/hooks/useLauncher';
import { PlatformTypes } from '@/types/gql.generated';
import { Heading, Text } from '@/ui';
import { useIsExtension } from '../../hooks/useIsExtension';
import { getRedirectLink } from './authLib';
import { HighlightPlatform } from './HighlightPlatform';
import type { LaunchReason } from './types';
import { useHandleAuthenticate } from './useHandleAuthenticate';

type Props = {
  reason: LaunchReason | null;
  platform: PlatformTypes | null;
  launch?: LaunchOption;
  launchParams?: LaunchParams;
  onComplete: () => void;
  resetOnSuccess?: boolean;
};

type HeroProps = Pick<Props, 'platform' | 'reason'>;

const HeroText = ({ reason, platform }: HeroProps) => {
  if (reason === 'bulk-add-to-calendar') {
    return 'Log in or sign up to email these events';
  }

  if (reason === 'add-to-calendar' && platform) {
    const text =
      platform === PlatformTypes.Email
        ? 'Log in or sign up to email this event'
        : 'Log in or sign up to add this event to {platform}';

    return <HighlightPlatform platform={platform} text={text} />;
  }

  if (reason === 'set-preferred-platform' && platform) {
    return (
      <HighlightPlatform
        platform={platform}
        text="Log in or sign up to pin {platform} as your calendar destination"
      />
    );
  }

  if (reason === 'create-share-link') {
    return 'Log in or sign up to create a shareable link';
  }

  if (reason === 'google-calendar-integration-multi') {
    return 'Connect your Google Calendar to add events';
  }

  if (reason === 'create-page-experience') {
    return 'Log in or sign up to create a calendar';
  }

  if (reason === 'canva-connect') {
    return 'Log in or sign up to create a shareable page from a Canva design';
  }

  return 'Log in or create an account';
};

export const AuthBody = ({
  reason,
  platform,
  onComplete,
  launch,
  launchParams,
  resetOnSuccess,
}: Props) => {
  const { base } = useCollection();
  const { isExtension } = useIsExtension();
  const { handleAuthenticate } = useHandleAuthenticate();

  const redirectTo =
    platform && reason === 'set-preferred-platform'
      ? getRedirectLink(
          isExtension,
          resetOnSuccess ? null : base,
          'set-preferred-platform',
          {
            platform,
          }
        )
      : getRedirectLink(
          isExtension,
          resetOnSuccess ? null : base,
          launch || 'magic-auth-success',
          {
            reason: reason ?? 'unknown',
            ...launchParams,
          }
        );
  const isGooglePlatform = platform
    ? [PlatformTypes.GoogleIntegration, PlatformTypes.GoogleLink].includes(
        platform
      )
    : false;

  return (
    <>
      <Text fontSize="72px" mb="6" textAlign="center">
        ✨
      </Text>

      <AuthPanel
        fullWidth
        hideLogo
        redirectTo={redirectTo}
        googleOptions={{
          authorizeCalendar:
            reason === 'google-calendar-integration-multi' ||
            (reason === 'add-to-calendar' && isGooglePlatform),
          googleOnly: reason === 'google-calendar-integration-multi',
        }}
        renderHeader={() => (
          <Heading
            as="h2"
            fontSize={{ base: '2xl', sm: '4xl' }}
            fontWeight="bold"
            mb="7"
            textAlign="center"
          >
            <HeroText platform={platform} reason={reason} />
          </Heading>
        )}
        onAuthenticated={(_action, result) => {
          const user = getAuthenticatedUser(result);

          handleAuthenticate({
            platform: platform || undefined,
            onComplete,
            user,
          });
        }}
      />
    </>
  );
};
